import {RetryLink} from '@apollo/client/link/retry';
import {ServerError, ServerParseError} from '@apollo/client';
import {ApolloMetricsLogger} from './apolloMetricsLogger';

const ERROR_METRIC_NAME = 'webflow.renderer.apollo.request.error';

export type RetryLinkConfig = {
  maxAttempts?: number;
  retryOnCorsErrors?: boolean;
  retriedServerErrors?: 'bad-gateway' | 'all';
  metricsLogger?: ApolloMetricsLogger;
};

export const createRetryLink = ({
  maxAttempts = 1,
  retryOnCorsErrors = true,
  retriedServerErrors = 'all',
  metricsLogger,
}: RetryLinkConfig): RetryLink => {
  return new RetryLink({
    attempts: (count, operation, error) => {
      const tags = [];
      let ret = false;
      if (count >= maxAttempts) {
        tags.push('max_attempts:true');
      } else {
        tags.push('max_attempts:false');
        if (
          error &&
          (ServerError.is(error) || ServerParseError.is(error)) &&
          ((retriedServerErrors === 'all' && error.statusCode >= 500) ||
            (retriedServerErrors === 'bad-gateway' && error.statusCode === 502))
        ) {
          // if the request fails, let's try it again because it was probably
          // a temporary issue.
          tags.push('reason:server_error');
          tags.push(`status_code:${error.statusCode}`);
          ret = true;
        } else if (
          retryOnCorsErrors &&
          error &&
          (ServerError.is(error) || ServerParseError.is(error)) &&
          error.bodyText
        ) {
          let parsedResult: unknown | null;

          try {
            parsedResult = JSON.parse(error.bodyText);
          } catch (e) {
            parsedResult = null;
          }

          if (
            parsedResult &&
            typeof parsedResult === 'object' &&
            'code' in parsedResult &&
            parsedResult.code === 'BadCrossOriginRequest'
          ) {
            // if CORS fails, it means we used a stale token so we can try it again
            // with the correct one
            tags.push('reason:cors_error');
            ret = true;
          }
        }
      }

      tags.push(`attempt:${count}`, `retry:${ret}`);
      metricsLogger?.logDistributionMetric(ERROR_METRIC_NAME, 1, ...tags);
      return ret;
    },
    delay: (count) => {
      // delay in ms
      return count * 500 + Math.random() * 500;
    },
  });
};
